/**
 * The HTTP side of the game: accounts, characters, servers, rankings and news. Kept apart from
 * the realtime client (net.ts) so the website can use it without loading Colyseus.
 */
import type { ClassId, Gender, Race } from "@two-crowns/data";
import { normalizeReferral } from "@two-crowns/shared";
import { API_URL, GAME_URL } from "./config";
import { lang } from "./i18n";
import { inApp, nativeHas, nativeProducts, nativeSignedOut } from "./native";

export interface CharacterSummary {
  id: string;
  serverId: string;
  name: string;
  race: Race;
  gender: Gender;
  cls: ClassId;
  level: number;
  zoneId: string;
  /** What it wears ("slot:item:plus|...", as in the game), for the character screen. */
  gear?: string;
  /** Its armor skin ("" = none). */
  skin?: string;
}

export interface NewCharacter {
  name: string;
  race: Race;
  gender: Gender;
  cls: ClassId;
}

const api = (path: string, init: RequestInit = {}) =>
  fetch(`${API_URL}${path}`, { ...init, credentials: "include" });

/**
 * The access token of the signed-in account from the httpOnly refresh cookie, or null when
 * this browser has no session yet. Never creates an account.
 */
export async function resumeSession(): Promise<string | null> {
  const res = await api("/auth/refresh", { method: "POST" });
  if (res.status === 403) {
    const { until } = (await res.json().catch(() => ({}))) as { until?: number };
    throw new BannedError(until ?? 0);
  }
  if (!res.ok) return null;
  // No token when this browser never signed in.
  return ((await res.json()) as { accessToken: string | null }).accessToken;
}

export interface ChannelInfo {
  channel: number;
  /** How busy the channel is, 0 to 1 in tenths (player counts are not published). */
  load: number;
  full: boolean;
}

export interface ServerInfo {
  id: string;
  name: string;
  /** Channels this server can open; the ones past `channels` open as the others fill up. */
  maxChannels: number;
  /** Open channels, in order. */
  channels: ChannelInfo[];
  /** At its player limit: new players wait in line. */
  full?: boolean;
  /** How many wait in line. */
  queue?: number;
}

/** A ticket in a server's line (serverGate.ts on the game server). */
export interface QueueTicket {
  ticket: string;
  /** Tickets ahead of this one (0 once it may enter). */
  position: number;
  ready: boolean;
}

const queueUrl = (serverId: string, ticket = "") =>
  `${GAME_URL.replace(/^ws/, "http")}/queue?server=${encodeURIComponent(serverId)}${
    ticket ? `&ticket=${encodeURIComponent(ticket)}` : ""
  }`;

/** Takes a ticket for the server's line (granted at once while there is room). */
export async function queueEnter(token: string, serverId: string): Promise<QueueTicket> {
  const res = await fetch(queueUrl(serverId), {
    method: "POST",
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`queue: ${res.status}`);
  return (await res.json()) as QueueTicket;
}

/** Where a ticket stands (null when it dropped out of the line). */
export async function queueStatus(
  token: string,
  serverId: string,
  ticket: string,
): Promise<QueueTicket | null> {
  const res = await fetch(queueUrl(serverId, ticket), {
    headers: { authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`queue: ${res.status}`);
  return (await res.json()) as QueueTicket;
}

/** Gives up a place in the line. */
export async function queueLeave(token: string, serverId: string, ticket: string) {
  await fetch(queueUrl(serverId, ticket), {
    method: "DELETE",
    headers: { authorization: `Bearer ${token}` },
  }).catch(() => undefined);
}

/** Item ids each monster can drop, by monster id (no chances: those stay on the server). */
export async function fetchDropLists(): Promise<Record<string, string[]>> {
  const res = await api("/database/drops");
  if (!res.ok) throw new Error(`drops: ${res.status}`);
  return ((await res.json()) as { drops: Record<string, string[]> }).drops;
}

/** Game servers with their open channels and load (from the game server). */
export async function fetchServers(): Promise<ServerInfo[]> {
  const res = await fetch(`${GAME_URL.replace(/^ws/, "http")}/servers`);
  if (!res.ok) throw new Error(`servers: ${res.status}`);
  return ((await res.json()) as { servers: ServerInfo[] }).servers;
}

export async function listCharacters(token: string, serverId: string): Promise<CharacterSummary[]> {
  const res = await api(`/characters?server=${encodeURIComponent(serverId)}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`characters: ${res.status}`);
  return ((await res.json()) as { characters: CharacterSummary[] }).characters;
}

export type CreateResult =
  | { ok: true; character: CharacterSummary }
  | { ok: false; error: "name_taken" | "character_limit" | "invalid" | "unknown" };

export async function createCharacter(
  token: string,
  serverId: string,
  input: NewCharacter,
): Promise<CreateResult> {
  const res = await api("/characters", {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({ ...input, server: serverId }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    character?: CharacterSummary;
    error?: string;
  };
  if (res.ok && body.character) return { ok: true, character: body.character };
  const error =
    body.error === "name_taken" || body.error === "character_limit" || body.error === "invalid"
      ? body.error
      : "unknown";
  return { ok: false, error };
}

export type DeleteResult =
  | "ok"
  | "name_mismatch"
  | "online"
  | "clan_leader"
  | "stall_open"
  | "not_found"
  | "unknown";

/** Deletes one of your characters; `name` is what the player typed to confirm. */
export async function deleteCharacter(
  token: string,
  id: string,
  name: string,
): Promise<DeleteResult> {
  const res = await api(`/characters/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (res.status === 204) return "ok";
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  const known = ["name_mismatch", "online", "clan_leader", "stall_open", "not_found"] as const;
  return known.find((k) => k === body.error) ?? "unknown";
}

export interface LeaderboardRow {
  name: string;
  race: Race;
  cls: ClassId;
  level: number;
  /** NP earned in the period. */
  np: number;
  /** Lifetime NP earned (rank symbol). */
  npEarned: number;
}

/** National Points ranking: this month or all time. */
export async function fetchLeaderboard(
  period: "month" | "all",
  serverId?: string,
  race?: "human" | "orc",
): Promise<LeaderboardRow[]> {
  const server = serverId ? `&server=${encodeURIComponent(serverId)}` : "";
  const nation = race ? `&race=${race}` : "";
  const res = await api(`/leaderboard?period=${period}${server}${nation}`);
  if (!res.ok) throw new Error(`leaderboard: ${res.status}`);
  return ((await res.json()) as { rows: LeaderboardRow[] }).rows;
}

/** The account is banned (moderation). */
export class BannedError extends Error {
  constructor(readonly until: number) {
    super(`banned until ${new Date(until).toISOString()}`);
  }
}

export interface AccountSummary {
  email: string | null;
  role: string;
}

/** The website's profile page: the account and its characters on every server. */
export interface AccountProfile extends AccountSummary {
  /** ms since epoch. */
  createdAt: number;
  gems: number;
  /** ms since epoch; 0 or past = no Gold Premium. */
  premiumUntil: number;
  /** Armor skins still running. */
  skins: string[];
  /** Each skin bought to the end of its time (ms since epoch). */
  skinUntil: Record<string, number>;
  /** The player turned off their characters' public share pages (/c/...). */
  shareHidden: boolean;
  /** The account proved its email (buying gems needs it). */
  emailVerified: boolean;
  /** An item lock PIN is set (the inn asks for it to lock or unlock an item). */
  itemPinSet: boolean;
  /** Invite friends: the link's code, how many came, how many earned the reward, its terms. */
  referral: { code: string; invited: number; rewarded: number; max: number; level: number };
  characters: {
    name: string;
    serverId: string;
    race: "human" | "orc";
    gender: "male" | "female";
    cls: string;
    level: number;
    zoneId: string;
  }[];
}

export async function accountProfile(token: string): Promise<AccountProfile | null> {
  const res = await api("/account", { headers: { authorization: `Bearer ${token}` } });
  return res.ok ? ((await res.json()) as AccountProfile) : null;
}

/** Turns the account's characters' public share pages on or off; false if it failed. */
export async function setSharePages(token: string, visible: boolean): Promise<boolean> {
  const res = await api("/account/share-pages", {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({ visible }),
  });
  return res.ok;
}

/** Sets the item lock PIN; null when done, else the api's error code. */
export async function setItemPin(
  token: string,
  pin: string,
  pin2: string,
  current?: string,
): Promise<string | null> {
  const res = await api("/account/item-pin", {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify(current ? { pin, pin2, current } : { pin, pin2 }),
  });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? "failed";
}

export type EpinResult =
  | { gems: number; balance: number }
  | "epin_unknown"
  | "epin_used"
  | "too_many_attempts"
  | "unknown";

/** Loads a prepaid E-pin code's gems onto the account (the profile page). */
export async function redeemEpin(token: string, code: string): Promise<EpinResult> {
  const res = await api("/account/epin", {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  const body = (await res.json().catch(() => ({}))) as {
    error?: string;
    gems?: number;
    balance?: number;
  };
  if (res.ok && typeof body.gems === "number" && typeof body.balance === "number")
    return { gems: body.gems, balance: body.balance };
  const known = ["epin_unknown", "epin_used", "too_many_attempts"] as const;
  return known.find((k) => k === body.error) ?? "unknown";
}

export async function me(token: string): Promise<AccountSummary | null> {
  const res = await api("/auth/me", { headers: { authorization: `Bearer ${token}` } });
  return res.ok ? ((await res.json()) as AccountSummary) : null;
}

/**
 * The share card's picture of a character (its game drew it): "hidden" while the player keeps
 * their share pages off (the profile turns them on), false if it was not taken.
 */
export async function uploadPortrait(characterId: string, png: Blob): Promise<boolean | "hidden"> {
  const token = await resumeSession().catch(() => null);
  if (!token) return false;
  const res = await api(`/characters/${characterId}/portrait`, {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "image/png" },
    body: png,
  });
  if (res.status === 403) return "hidden";
  return res.ok;
}

/** Spends the email check link's token: true when the address now counts as proven. */
export async function verifyEmailLink(token: string): Promise<boolean> {
  const res = await api("/auth/verify", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  }).catch(() => null);
  return !!res?.ok;
}

/** Sends the email check link again: null when sent, else an error code. */
export async function resendVerification(token: string): Promise<string | null> {
  const res = await api("/account/verify-email", {
    method: "POST",
    headers: { authorization: `Bearer ${token}` },
  }).catch(() => null);
  if (res?.ok) return null;
  const body = (await res?.json().catch(() => ({}))) as { error?: string } | undefined;
  return body?.error ?? "unknown";
}

/** Deletes the signed-in account (email typed to confirm, and the password): null when done. */
export async function deleteAccount(
  token: string,
  confirm: string,
  password: string,
): Promise<string | null> {
  const res = await api("/account/delete", {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    body: JSON.stringify({ confirm, ...(password ? { password } : {}) }),
  }).catch(() => null);
  if (res?.ok) return null;
  const body = (await res?.json().catch(() => ({}))) as { error?: string } | undefined;
  return body?.error ?? "unknown";
}

/** Which gem packs can be bought with money now (Polar); none while payments are off. */
export type PayCurrency = "usd" | "try";

export interface PaymentInfo {
  enabled: boolean;
  packs: string[];
  currency?: PayCurrency;
  /**
   * In the iOS app: packs are sold by the App Store, each under its product id, at the App
   * Store's price in the player's own currency (never Polar's).
   */
  appStore?: { products: Record<string, string>; prices: Record<string, string> };
}

/**
 * Which gem packs are on sale, and the currency Polar will charge this visitor; in the iOS app,
 * the App Store's packs and prices instead (nothing when the app cannot sell).
 */
export async function paymentInfo(): Promise<PaymentInfo> {
  if (inApp()) return appStoreInfo();
  const res = await api("/store/payments").catch(() => null);
  if (!res?.ok) return { enabled: false, packs: [] };
  return (await res.json()) as PaymentInfo;
}

async function appStoreInfo(): Promise<PaymentInfo> {
  const none = { enabled: false, packs: [] };
  if (!nativeHas("iap")) return none;
  const res = await api("/store/apple").catch(() => null);
  if (!res?.ok) return none;
  const body = (await res.json()) as { enabled: boolean; products: Record<string, string> };
  if (!body.enabled) return none;
  const listed = await nativeProducts(Object.values(body.products)).catch(() => []);
  const prices: Record<string, string> = {};
  for (const [pack, id] of Object.entries(body.products)) {
    const p = listed.find((x) => x.id === id);
    if (p) prices[pack] = p.price;
  }
  // A pack the App Store does not know (not approved yet) is not on sale.
  const packs = Object.keys(prices);
  return {
    enabled: packs.length > 0,
    packs,
    appStore: { products: body.products, prices },
  };
}

/**
 * A gem pack's price as its checkout will ask it: TRY from Turkey, USD elsewhere (what the
 * api says). Until the api answers, Turkish pages guess TRY.
 */
export function packPrice(p: { price: string; priceTry: string }, currency?: PayCurrency) {
  return (currency ?? (lang() === "tr" ? "try" : "usd")) === "try" ? p.priceTry : p.price;
}

/** The signed-in account has an email it has not confirmed yet (gems are sold only once it is). */
export async function emailUnconfirmed(): Promise<boolean> {
  const token = await resumeSession().catch(() => null);
  const a = token ? await accountProfile(token).catch(() => null) : null;
  return Boolean(a?.email && !a.emailVerified);
}

/** A Polar checkout page for a gem pack (the signed-in account's), or an error code. */
export async function startCheckout(pack: string): Promise<{ url: string } | { error: string }> {
  // The iOS app sells through the App Store only.
  if (inApp()) return { error: "checkout_failed" };
  const token = await resumeSession().catch(() => null);
  if (!token) return { error: "unauthorized" };
  const res = await api("/store/checkout", {
    method: "POST",
    headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
    // Polar's checkout in the page's language, whatever the browser's is.
    body: JSON.stringify({ pack, locale: lang() }),
  }).catch(() => null);
  if (!res) return { error: "checkout_failed" };
  const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
  return body.url ? { url: body.url } : { error: body.error ?? "checkout_failed" };
}

/**
 * Opens a gem pack's checkout in a new tab. The tab opens on the click itself (a tab opened
 * after a request is blocked as a pop-up) and is sent to Polar once the api answers; false if
 * it could not be made.
 */
export async function openCheckout(pack: string): Promise<true | string> {
  const tab = window.open("", "_blank");
  const r = await startCheckout(pack);
  if ("url" in r) {
    if (tab) tab.location.href = r.url;
    else location.href = r.url;
    return true;
  }
  tab?.close();
  return r.error;
}

/** The invite code from a friend's link (/?ref=CODE), kept this long until a sign-up. */
const INVITE_KEY = "wc.ref";
const INVITE_KEEP_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Keeps the invite code of a friend's link for the sign-up, which may come days later, and
 * takes it out of the address (every site page calls this).
 */
export function rememberInvite() {
  const url = new URL(location.href);
  const raw = url.searchParams.get("ref");
  if (raw === null) return;
  const code = normalizeReferral(raw);
  try {
    if (code) localStorage.setItem(INVITE_KEY, JSON.stringify({ code, at: Date.now() }));
  } catch {
    // Storage blocked: the link only counts if the sign-up is in this page.
    if (code) sessionInvite = code;
  }
  url.searchParams.delete("ref");
  history.replaceState(history.state, "", url.pathname + url.search + url.hash);
}
let sessionInvite: string | null = null;

/** The invite code to send with a sign-up, if a friend's link brought this browser. */
function pendingInvite(): string | undefined {
  try {
    const saved = JSON.parse(localStorage.getItem(INVITE_KEY) ?? "null") as {
      code?: string;
      at?: number;
    } | null;
    if (saved?.code && Date.now() - (saved.at ?? 0) < INVITE_KEEP_MS) return saved.code;
  } catch {
    // Unreadable: no invite.
  }
  return sessionInvite ?? undefined;
}

function inviteUsed() {
  sessionInvite = null;
  try {
    localStorage.removeItem(INVITE_KEY);
  } catch {
    // Nothing kept.
  }
}

/**
 * Creates an account and signs this browser in to it (there is no guest play). Returns an
 * error code or null; resumeSession() then gives the access token.
 */
export async function signup(
  email: string,
  password: string,
  captcha?: string | null,
): Promise<string | null> {
  const ref = pendingInvite();
  const res = await api("/auth/signup", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      email,
      password,
      ...(captcha ? { captcha } : {}),
      ...(ref ? { ref } : {}),
    }),
  });
  if (res.ok) {
    inviteUsed();
    return null;
  }
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? "unknown";
}

/** Signs in to an account; the page then reloads into it. Error code or null. */
export async function login(email: string, password: string): Promise<string | null> {
  const res = await api("/auth/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? "unknown";
}

/**
 * Asks for a password reset email. The API answers the same whether the address has an
 * account or not. Error code or null.
 */
export async function forgotPassword(
  email: string,
  captcha?: string | null,
): Promise<string | null> {
  const res = await api("/auth/forgot", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, ...(captcha ? { captcha } : {}) }),
  });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? "unknown";
}

/** Sets a new password with the token from the reset email and signs in. Error code or null. */
export async function resetPassword(token: string, password: string): Promise<string | null> {
  const res = await api("/auth/reset", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  if (res.ok) return null;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? "unknown";
}

/** Signs in with Apple in the iOS app (makes the account the first time). */
export async function appleLogin(s: {
  idToken: string;
  nonce: string;
  code?: string;
}): Promise<string | null> {
  const ref = pendingInvite();
  const res = await api("/auth/apple", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ ...s, ...(ref ? { ref } : {}) }),
  }).catch(() => null);
  if (res?.ok) {
    inviteUsed();
    return null;
  }
  const body = (await res?.json().catch(() => ({}))) as { error?: string } | undefined;
  return body?.error ?? "unknown";
}

/** Signs in with the credential from Google's button (makes the account the first time). */
export async function googleLogin(credential: string): Promise<string | null> {
  const ref = pendingInvite();
  const res = await api("/auth/google", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ credential, ...(ref ? { ref } : {}) }),
  });
  if (res.ok) {
    // Only a new account takes the invite; signing in to an old one leaves it unused, harmless.
    inviteUsed();
    return null;
  }
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? "unknown";
}

export async function logout(): Promise<void> {
  nativeSignedOut();
  await api("/auth/logout", { method: "POST" }).catch(() => undefined);
}

export interface NewsPost {
  id: number;
  kind: "news" | "update" | "event" | "maintenance";
  title: string;
  body: string;
  pinned: boolean;
  publishedAt: string;
}

/** Website posts, pinned first, then newest. */
export async function fetchNews(limit = 10): Promise<NewsPost[]> {
  const res = await api(`/news?limit=${limit}`);
  if (!res.ok) throw new Error(`news: ${res.status}`);
  return ((await res.json()) as { posts: NewsPost[] }).posts;
}

export interface ClanRankRow {
  name: string;
  race: Race;
  members: number;
  np: number;
  topLevel: number;
}

export async function fetchClanRanking(serverId: string): Promise<ClanRankRow[]> {
  const res = await api(`/clans/ranking?server=${encodeURIComponent(serverId)}`);
  if (!res.ok) throw new Error(`clans: ${res.status}`);
  return ((await res.json()) as { rows: ClanRankRow[] }).rows;
}
